import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, ArrowDownRight, Coins, History, RotateCcw, Clock, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import PlayItBackDialog from '../components/PlayItBackDialog';
import BrandedReceipt from '../components/BrandedReceipt';
import { useShowMore } from '../components/useShowMore';
import { FreePlayNote, playable } from '../components/FreePlayNote';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '—';
    return date.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
};

// Play-back is player-initiated and self-serve while the cashout is still
// PENDING or APPROVED but NOT yet paid out. Once cash is actually sent (paid),
// support must handle any reversal. Relaxed 2026-08-17 (was pending-only from
// 2026-08-01) so players can reclaim an approved-but-unpaid cashout on their own.
const isPlayableBack = (tx) =>
    tx?.transaction_type === 'cashout_request' &&
    (tx?.status === 'pending' || tx?.status === 'approved') &&
    tx?.paid !== true;

const Transactions = () => {
    const { user, refreshUser } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [dialogTx, setDialogTx] = useState(null);
    const [localBalance, setLocalBalance] = useState(null);
    const [receiptTx, setReceiptTx] = useState(null);

    const fetchTransactions = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/user/transactions`, { withCredentials: true });
            setTransactions(Array.isArray(data) ? data : []);
        } catch (error) {
            console.error('Error fetching transactions:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

    const openPlayItBack = (tx) => setDialogTx(tx);
    const closeDialog = (open) => { if (!open) setDialogTx(null); };

    const handleConfirmPlayBack = async ({ amount, note: _note }) => {
        if (!dialogTx) return;
        setBusy(true);
        try {
            const body = { transaction_id: dialogTx.id };
            if (amount != null) body.amount = amount;
            const { data } = await axios.post(
                `${API}/user/cashout-play-back`,
                body,
                { withCredentials: true }
            );
            const msg = data.partial
                ? `$${data.refunded.toFixed(2)} put back — $${data.remaining_cashout.toFixed(2)} still pending`
                : `$${data.refunded.toFixed(2)} put back in your balance — happy playing!`;
            toast.success(msg + (data.bonus_granted ? ` · +$${data.bonus_granted.toFixed(2)} Free Play bonus!` : ''), { duration: 6000 });
            setDialogTx(null);
            // Update the visible balance immediately from the authoritative
            // response — /auth/me is cached ~3s so refreshUser() alone leaves
            // the header stale until a manual reload.
            if (data.new_balance != null) setLocalBalance(Number(data.new_balance) + Number(user?.bonus_credits || 0));
            await fetchTransactions();
            try { await refreshUser?.(); } catch { /* AuthContext may not expose refreshUser */ }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not play it back — please try again');
        }
        setBusy(false);
    };

    const { visibleItems, ShowMoreButton, total, limit } = useShowMore(transactions, 10, 10);

    const getTransactionIcon = (amount) => (
        (amount ?? 0) > 0
            ? <ArrowUpRight className="w-5 h-5 text-[#39FF14]" />
            : <ArrowDownRight className="w-5 h-5 text-[#FF3B30]" />
    );
    const getTransactionColor = (amount) =>
        (amount ?? 0) > 0 ? 'text-[#39FF14]' : 'text-[#FF3B30]';

    return (
        <div className="min-h-screen bg-[#050505]">
            <Header />

            <div className="max-w-4xl mx-auto px-4 py-8">
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black text-white mb-2">
                            Transaction <span className="text-[#FFD700]">History</span>
                        </h1>
                        <p className="text-[#A0AAB2]">View all your gaming activity</p>
                    </div>
                    <div className="card-dark px-4 py-3">
                        <p className="text-xs text-[#A0AAB2] mb-1">Current Balance</p>
                        <div className="flex items-center gap-2">
                            <Coins className="w-5 h-5 text-[#FFD700]" />
                            <span className="text-xl font-mono font-bold text-[#FFD700]" data-testid="balance">
                                {(localBalance ?? playable(user)).toFixed(2)}
                            </span>
                        </div>
                        <FreePlayNote user={user} testId="transactions-free-play" className="mt-1" />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className="spinner" />
                    </div>
                ) : transactions.length === 0 ? (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="card-dark p-12 text-center"
                    >
                        <History className="w-16 h-16 text-[#A0AAB2] mx-auto mb-4" />
                        <h3 className="text-xl font-bold text-white mb-2">No Transactions Yet</h3>
                        <p className="text-[#A0AAB2]">Start playing to see your history here!</p>
                    </motion.div>
                ) : (
                    <>
                    <div className="space-y-3">
                        {visibleItems.map((tx, index) => {
                            const playable = isPlayableBack(tx);
                            // For a pending cashout, show the NET the player will actually
                            // receive (after the 6% fee) so it matches the cashier's "SEND THIS".
                            const isPendingCashout = tx.transaction_type === 'cashout_request'
                                && tx.status === 'pending' && tx.net_amount != null && Number(tx.net_amount) > 0;
                            const displayAmt = isPendingCashout ? -Math.abs(Number(tx.net_amount)) : (tx.amount ?? 0);
                            return (
                                <motion.div
                                    key={tx.id || index}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    className={`card-dark p-4 ${playable ? 'border-[#00E0FF]/30' : ''}`}
                                    data-testid={`transaction-${index}`}
                                >
                                    <div className="flex items-center justify-between gap-4 flex-wrap">
                                        <div className="flex items-center gap-4 min-w-0">
                                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                                tx.amount > 0 ? 'bg-[#39FF14]/10' : 'bg-[#FF3B30]/10'
                                            }`}>
                                                {getTransactionIcon(tx.amount)}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-white font-medium truncate">{tx.description || tx.transaction_type || 'Transaction'}</p>
                                                <p className="text-[#A0AAB2] text-sm flex items-center gap-2 flex-wrap">
                                                    <span>{formatDate(tx.created_at)}</span>
                                                    {playable && (
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/30">
                                                            <Clock className="w-3 h-3" /> Waiting for payout
                                                        </span>
                                                    )}
                                                    {tx.status === 'played_back' && (
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#00E0FF]/15 text-[#00E0FF] border border-[#00E0FF]/30">
                                                            <RotateCcw className="w-3 h-3" /> Played back
                                                        </span>
                                                    )}
                                                    {tx.status === 'approved' && tx.paid && (
                                                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/30">
                                                            Paid
                                                        </span>
                                                    )}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="text-right">
                                            <p className={`font-mono font-bold ${getTransactionColor(displayAmt)}`}>
                                                {displayAmt > 0 ? '+' : ''}{displayAmt.toFixed(2)}
                                            </p>
                                            {isPendingCashout && (
                                                <p className="text-[#FFD700] text-[11px] font-semibold" data-testid={`tx-net-note-${index}`}>
                                                    you'll receive ${Math.abs(Number(tx.net_amount)).toFixed(2)}
                                                </p>
                                            )}
                                            <p className="text-[#A0AAB2] text-sm">
                                                Bal: {(tx.balance_after ?? 0).toFixed(2)}
                                            </p>
                                        </div>
                                    </div>
                                    {playable && (
                                        <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-between gap-3 flex-wrap">
                                            <p className="text-xs text-[#A0AAB2]">
                                                Don't want to wait? Put some — or all — of it back and keep playing.
                                            </p>
                                            <Button
                                                size="sm"
                                                onClick={() => openPlayItBack(tx)}
                                                className="bg-[#00E0FF] text-black hover:bg-[#00E0FF]/80 font-bold"
                                                data-testid={`play-it-back-${tx.id}`}
                                            >
                                                <RotateCcw className="w-4 h-4 mr-1" />
                                                Cancel &amp; Play It Back
                                            </Button>
                                        </div>
                                    )}
                                    {tx.transaction_type === 'cashout_request' && (
                                        <div className="mt-2 flex justify-end">
                                            <button
                                                type="button"
                                                onClick={() => setReceiptTx(tx)}
                                                className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#FFD700]/90 hover:text-[#FFD700] transition-colors"
                                                data-testid={`save-receipt-${tx.id}`}
                                            >
                                                <Download className="w-3.5 h-3.5" /> Save receipt
                                            </button>
                                        </div>
                                    )}
                                </motion.div>
                            );
                        })}
                    </div>
                    <p className="text-[10px] text-[#A0AAB2] text-center mt-3">
                        Showing {Math.min(limit, total)} of {total}
                    </p>
                    <ShowMoreButton testid="player-transactions-show-more" />
                    </>
                )}
            </div>

            <PlayItBackDialog
                open={!!dialogTx}
                onOpenChange={closeDialog}
                maxAmount={dialogTx ? Math.abs(dialogTx.amount || 0) : 0}
                isAdmin={false}
                busy={busy}
                onConfirm={handleConfirmPlayBack}
            />

            <BrandedReceipt
                open={!!receiptTx}
                onClose={() => setReceiptTx(null)}
                receipt={receiptTx ? (() => {
                    const gross = Math.abs(Number(receiptTx.amount) || 0);
                    const net = receiptTx.net_amount != null ? Math.abs(Number(receiptTx.net_amount)) : gross;
                    return {
                        kind: 'cashout',
                        gross,
                        fee: Math.max(0, Math.round((gross - net) * 100) / 100),
                        net,
                        method: receiptTx.payout_method || '',
                        handle: receiptTx.payout_handle || '',
                        etaHours: null,
                        afterHours: false,
                        date: receiptTx.created_at,
                        reference: String(receiptTx.id || '').slice(-8),
                    };
                })() : null}
            />
        </div>
    );
};

export default Transactions;
